import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  SHOP_PACK_PRICE,
  isShopPackBuyQty,
  isShopSellId,
  type AiDeckDifficulty,
  type DuelOutcome,
  type DuelXpResult,
  type PackId,
  type DailyMissionsSnapshot,
} from '@shared/index'
import { api, ApiError } from '../api/http'
import { useAuthStore } from './auth'
import { useCollectionStore } from './collection'
import { useProgressStore } from './progress'
import { useMissionsStore } from './missions'
import { useRankedLobbyStore } from './rankedLobby'

type WalletPayload = {
  coins: number
  packPrice?: number
  packs?: Record<PackId, number>
}

type PayoutPayload = {
  coins: number
  delta: number
  progress?: DuelXpResult
  packs?: Record<PackId, number>
  missions?: DailyMissionsSnapshot
  rankedOffer?: { coins: number; pending: number }
}

/**
 * Server-backed coin balance. Not persisted locally — the account owns it.
 */
export const useWalletStore = defineStore('wallet', () => {
  const coins = ref(0)
  const busy = ref(false)
  const error = ref<string | null>(null)

  function clear(): void {
    coins.value = 0
    error.value = null
    busy.value = false
  }

  function setCoins(value: number): void {
    coins.value = Math.max(0, value)
  }

  /**
   * Loads the account balance. No-op without a session.
   */
  async function refresh(): Promise<void> {
    const auth = useAuthStore()
    if (!auth.token) {
      clear()
      return
    }
    try {
      const payload = await api<WalletPayload>('/economy/wallet', {
        token: auth.token,
      })
      coins.value = payload.coins
      error.value = null
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clear()
        return
      }
      error.value = err instanceof ApiError ? err.code : 'network'
    }
  }

  /**
   * Credits vs-AI coins. Leave / Back must not call this.
   */
  async function reportAiPayout(input: {
    outcome: DuelOutcome
    difficulty: AiDeckDifficulty
    surrendered: boolean
    metrics?: import('@shared/index').DuelMissionMetrics
    durationMs?: number
    begunAt?: number
    cardResult?: 'win' | 'loss' | 'draw'
  }): Promise<{
    delta: number
    progress: DuelXpResult | null
    rankedOffer: { coins: number; pending: number } | null
  }> {
    const auth = useAuthStore()
    if (!auth.token) return { delta: 0, progress: null, rankedOffer: null }
    try {
      const payload = await api<PayoutPayload>('/economy/payout', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ kind: 'ai', ...input }),
      })
      coins.value = payload.coins
      error.value = null
      if (payload.packs) useCollectionStore().applyPacks(payload.packs)
      if (payload.progress) {
        useProgressStore().applyDuelResult(payload.progress)
      }
      if (payload.missions) useMissionsStore().apply(payload.missions)
      if (payload.rankedOffer) {
        useRankedLobbyStore().setOfferPending(payload.rankedOffer.pending)
      }
      return {
        delta: payload.delta,
        progress: payload.progress ?? null,
        rankedOffer: payload.rankedOffer ?? null,
      }
    } catch (err) {
      error.value = err instanceof ApiError ? err.code : 'network'
      return { delta: 0, progress: null, rankedOffer: null }
    }
  }

  /**
   * Debits price × qty and credits that many unopened LOB or Metal Raiders packs.
   * `qty` is 1, 3, 5, or 10. `sent` is false when the browser refused before
   * the request, so the caller can record that click itself.
   */
  async function buyShopPack(
    packId: PackId,
    qty = 1,
  ): Promise<
    { ok: true } | { ok: false; code: string; sent: boolean }
  > {
    const auth = useAuthStore()
    if (!auth.token) return { ok: false, code: 'signed_out', sent: false }
    if (busy.value) return { ok: false, code: 'busy', sent: false }
    if (!isShopSellId(packId) || !isShopPackBuyQty(qty)) {
      return { ok: false, code: 'invalid_pack', sent: false }
    }
    if (coins.value < SHOP_PACK_PRICE * qty) {
      return { ok: false, code: 'insufficient_coins', sent: false }
    }
    busy.value = true
    try {
      const payload = await api<WalletPayload & { packId: string }>(
        '/economy/buy-pack',
        {
          method: 'POST',
          token: auth.token,
          body: JSON.stringify({ packId, qty }),
        },
      )
      coins.value = payload.coins
      if (payload.packs) useCollectionStore().applyPacks(payload.packs)
      error.value = null
      return { ok: true }
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'network'
      error.value = code
      return { ok: false, code, sent: true }
    } finally {
      busy.value = false
    }
  }

  /**
   * DevTools: credit 100 coins. Needs the unlock password.
   */
  async function grantDevCoins(password: string): Promise<boolean> {
    const auth = useAuthStore()
    if (!auth.token) return false
    try {
      const payload = await api<{ coins: number }>('/economy/dev-grant-coins', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ password }),
      })
      coins.value = payload.coins
      error.value = null
      return true
    } catch {
      return false
    }
  }

  return {
    coins,
    busy,
    error,
    clear,
    setCoins,
    refresh,
    reportAiPayout,
    buyShopPack,
    grantDevCoins,
  }
})
